import { ComponentProps, ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { UserCircleIcon } from "@heroicons/react/outline";
import { FormikProps } from "formik";
import { useCustomFields } from "src/hooks/useContactCustomField";
import SelectorField from "../../components/Form/SelectorField";
import TextInputField from "../../components/Form/TextInputField";
import { getContactVariables } from "../../helpers/utils";
import FieldAddVariableContainer from "../Form/FieldAddVariableContainer";
import InputActionButton from "../Form/InputActionButton";
import HTTPFormBody from "./HTTPFormBody";
import HTTPFormHeaders from "./HTTPFormHeaders";
import HTTPFormNav from "./HTTPFormNav";
import HTTPFormParams from "./HTTPFormParams";
import { createAvailableVariables, formatOptionLabel } from "./HTTPFormUtils";
import { AGENT_VARIABLES, HTTPFormMeta } from "./Meta";

export interface HTTPFormProps {
  formik: FormikProps<any>;
  formPrefix: string;
  formValues: {
    method: string;
    url: string;
    headers: { key: string; value: string }[];
    params: { key: string; value: string }[];
    body: string;
    body_type: string;
    body_params: { key: string; value: string }[];
  };
  availableVariables: {
    label: string;
    icon: (props: ComponentProps<"svg">) => ReactElement;
    options: {
      label: string;
      value: string;
    }[];
  }[];
}

export default function HTTPForm({
  formik,
  formPrefix,
  formValues,
}: Omit<HTTPFormProps, "availableVariables">) {
  const [currentStep, setCurrentStep] = useState("body");
  const { t } = useTranslation();
  const { contactCustomFields } = useCustomFields();

  const availableVariables = useMemo(() => {
    const contactVars = getContactVariables(contactCustomFields);
    return createAvailableVariables(
      t,
      contactVars,
      AGENT_VARIABLES,
      UserCircleIcon
    );
  }, [t, contactCustomFields]);

  return (
    <>
      <SelectorField
        label={t(HTTPFormMeta.FIELDS.method.label)}
        name={`${formPrefix}.${HTTPFormMeta.FIELDS.method.key}`}
        help={t(HTTPFormMeta.FIELDS.method.help)}
        options={HTTPFormMeta.METHOD_TYPES}
        // @ts-ignore
        formatOptionLabel={(props) => formatOptionLabel({ ...props, t })}
      />
      <FieldAddVariableContainer
        formik={formik}
        fieldName={`${formPrefix}.${HTTPFormMeta.FIELDS.url.key}`}
        currentValue={formValues[HTTPFormMeta.FIELDS.url.key]}
        availableVariables={availableVariables}
      >
        {(setShowVariableSelect) => (
          <TextInputField
            label={t(HTTPFormMeta.FIELDS.url.label)}
            name={`${formPrefix}.${HTTPFormMeta.FIELDS.url.key}`}
            placeholder={t(HTTPFormMeta.FIELDS.url.placeholder)}
            type="text"
            action={
              <InputActionButton
                onClick={() => setShowVariableSelect(true)}
                type="button"
              >
                {t("inbox-actions:builder.add-variable", "Add variable")}
              </InputActionButton>
            }
          />
        )}
      </FieldAddVariableContainer>

      <HTTPFormNav currentStep={currentStep} setCurrentStep={setCurrentStep} />
      {currentStep === "headers" && (
        <HTTPFormHeaders
          formik={formik}
          formPrefix={formPrefix}
          formValues={formValues}
          availableVariables={availableVariables}
        />
      )}
      {currentStep === "query" && (
        <HTTPFormParams
          formik={formik}
          formPrefix={formPrefix}
          formValues={formValues}
          availableVariables={availableVariables}
        />
      )}
      {currentStep === "body" && (
        <HTTPFormBody
          formik={formik}
          formPrefix={formPrefix}
          formValues={formValues}
          availableVariables={availableVariables}
        />
      )}
    </>
  );
}
